import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { resolveColor } from "../utils/colors";
import {
  Users,
  Plus,
  ArrowLeft,
  Search,
  Loader2,
  UserPlus,
  Settings,
  Trash2,
  Building2,
  Shield,
  X,
  Check,
  FileText,
  BarChart3,
  GripVertical,
  Pencil,
  Home,
  Car,
  HeartPulse,
  GitBranch,
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, isAdvisor, getUserRole } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

// Default lead stages (can be customized)
const DEFAULT_STAGES = [
  { id: "new", name: "New Lead", color: "#64748b", order: 1 },
  { id: "first_meeting", name: "First Meeting", color: "#3b82f6", order: 2 },
  { id: "quote_sent", name: "Quote Sent", color: "#8b5cf6", order: 3 },
  { id: "proposal", name: "Proposal", color: "#f59e0b", order: 4 },
  { id: "login_done", name: "Login Done", color: "#06b6d4", order: 5 },
  { id: "policy_issued", name: "Policy Issued", color: "#22c55e", order: 6 },
  { id: "closed_won", name: "Closed Won", color: "#10b981", order: 7 },
  { id: "postponed", name: "Postponed", color: "#f97316", order: 8 },
  { id: "closed_lost", name: "Closed Lost", color: "#ef4444", order: 9 },
];

// Determine if a color is dark (needs white text) or light (needs dark text)
const getContrastTextColor = (hexColor) => {
  if (!hexColor) return "#ffffff";
  const colorNameMap = {
    cyan: "#00FFFF", aqua: "#00FFFF", teal: "#008080", turquoise: "#40E0D0",
    yellow: "#FFFF00", lime: "#00FF00", green: "#008000", white: "#FFFFFF",
    lightgray: "#D3D3D3", lightgrey: "#D3D3D3", silver: "#C0C0C0", orange: "#FFA500",
    gold: "#FFD700", pink: "#FFC0CB", hotpink: "#FF69B4", red: "#FF0000",
    blue: "#0000FF", navy: "#000080", purple: "#800080", black: "#000000",
    gray: "#808080", grey: "#808080",
  };
  let hex = colorNameMap[hexColor.toLowerCase()] || hexColor;
  hex = hex.replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(hex) && !/^[0-9A-Fa-f]{3}$/.test(hex)) return "#ffffff";
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#1e293b" : "#ffffff";
};

// Insurance lead types (bifurcation)
const INSURANCE_TYPES = [
  { value: "health", label: "Health", color: "#047857", bg: "rgba(16,185,129,0.15)", icon: HeartPulse },
  { value: "life", label: "Life", color: "#1d4ed8", bg: "rgba(59,130,246,0.15)", icon: Shield },
  { value: "vehicle", label: "Vehicle", color: "#b45309", bg: "rgba(245,158,11,0.15)", icon: Car },
  { value: "other", label: "Other", color: "#6d28d9", bg: "rgba(167,139,250,0.15)", icon: FileText },
];

const getInsuranceTypeInfo = (value) => INSURANCE_TYPES.find((t) => t.value === value) || null;

const InsuranceTypeBadge = ({ type, className = "" }) => {
  const info = getInsuranceTypeInfo(type);
  if (!info) return null;
  const Icon = info.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${className}`}
      style={{ backgroundColor: info.bg, color: info.color }}
      data-testid={`insurance-badge-${type}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {info.label}
    </span>
  );
};

// Reference / source badge shown next to each lead
const getSourceLabel = (lead, sources) => {
  const src = (lead.source || "").trim();
  if (!src) return null;
  if (src.toLowerCase() === "associate") {
    return lead.associate_name ? `Assoc: ${lead.associate_name}` : "Associate";
  }
  const found = (sources || []).find(
    (s) => (s.value || s.id) === src || (s.label || s.name || "").toLowerCase() === src.toLowerCase()
  );
  return found ? found.label || found.name : src;
};

const SourceBadge = ({ lead, sources }) => {
  const label = getSourceLabel(lead, sources);
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-[#EDF1F8] text-[#41527A] border border-[#C9D4E6]"
      data-testid={`lead-source-badge-${lead.id}`}
    >
      <GitBranch className="h-2.5 w-2.5" />
      {label}
    </span>
  );
};

// Default lead sources (will be overwritten by API data)
const DEFAULT_LEAD_SOURCES = [
  { value: "direct", label: "Direct" },
  { value: "referral", label: "Referral" },
  { value: "associate", label: "Associate" },
  { value: "beshak", label: "Beshak" },
];

const INACTIVE_STAGE_IDS = ["lost", "closed_lost", "lost_potential", "lost-potential", "lost - potential"];
const CONVERTED_STAGE_IDS = ["converted", "won", "policy_issued", "policyissued"];
const POSTPONED_STAGE_IDS = ["postponed", "postpone", "on_hold", "onhold", "paused", "hold"];

const isInactiveStage = (statusOrStage) => {
  if (!statusOrStage) return false;
  const statusStr = typeof statusOrStage === "object" ? statusOrStage.id || statusOrStage.name || "" : statusOrStage;
  const normalizedStatus = statusStr.toLowerCase().replace(/[\s\-_]+/g, "");
  if (normalizedStatus.includes("lost")) return true;
  return INACTIVE_STAGE_IDS.some((id) => normalizedStatus === id.replace(/[\s\-_]+/g, ""));
};

const isConvertedStage = (statusOrStage) => {
  if (!statusOrStage) return false;
  const statusStr = typeof statusOrStage === "object" ? statusOrStage.id || statusOrStage.name || "" : statusOrStage;
  const normalizedStatus = statusStr.toLowerCase().replace(/[\s\-_]+/g, "");
  if (normalizedStatus.includes("converted")) return true;
  if (normalizedStatus === "won") return true;
  return CONVERTED_STAGE_IDS.some((id) => normalizedStatus === id.replace(/[\s\-_]+/g, ""));
};

const isPostponedStage = (statusOrStage) => {
  if (!statusOrStage) return false;
  const statusStr = typeof statusOrStage === "object" ? statusOrStage.id || statusOrStage.name || "" : statusOrStage;
  const normalizedStatus = statusStr.toLowerCase().replace(/[\s\-_]+/g, "");
  if (normalizedStatus.includes("postpon")) return true;
  return POSTPONED_STAGE_IDS.some((id) => normalizedStatus === id.replace(/[\s\-_]+/g, ""));
};

const isInactiveStageObject = (stage) => {
  if (!stage) return false;
  return isInactiveStage(stage.id) || isInactiveStage(stage.name);
};

const isConvertedStageObject = (stage) => {
  if (!stage) return false;
  return isConvertedStage(stage.id) || isConvertedStage(stage.name);
};

const isPostponedStageObject = (stage) => {
  if (!stage) return false;
  return isPostponedStage(stage.id) || isPostponedStage(stage.name);
};

const EMPTY_LEAD_FORM = {
  name: "",
  phone: "",
  email: "",
  source: "direct",
  insurance_type: "health",
  associate_name: "",
  notes: "",
};

export default function LeadManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [riders, setRiders] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [leadSources, setLeadSources] = useState(DEFAULT_LEAD_SOURCES);
  const [associates, setAssociates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showSidebar, setShowSidebar] = useState(false);

  const [leadTypeFilter, setLeadTypeFilter] = useState("active"); // active | converted | inactive
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState("all");

  // Lead add/edit modal (single modal handles both)
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [leadForm, setLeadForm] = useState(EMPTY_LEAD_FORM);
  const [saving, setSaving] = useState(false);

  // Settings + stages management
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showStagesModal, setShowStagesModal] = useState(false);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editStageName, setEditStageName] = useState("");
  const [editStageColor, setEditStageColor] = useState("#3b82f6");

  const [newCompany, setNewCompany] = useState("");
  const [newRider, setNewRider] = useState("");
  const [newStageName, setNewStageName] = useState("");
  const [newStageColor, setNewStageColor] = useState("#3b82f6");
  const [newSource, setNewSource] = useState("");
  const [newAssociate, setNewAssociate] = useState("");

  const userRole = getUserRole();
  const canManage = isAdvisor(); // Admin-only: settings, delete
  const canEditLeads = !!userRole; // Admin + Employee: create/edit lead, change stage

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, companiesRes, ridersRes, stagesRes, sourcesRes, associatesRes] = await Promise.all([
        authGet("/api/advisor/leads"),
        authGet("/api/advisor/settings/companies"),
        authGet("/api/advisor/settings/riders"),
        authGet("/api/advisor/settings/stages"),
        authGet("/api/advisor/settings/sources"),
        authGet("/api/advisor/settings/associates"),
      ]);
      if (leadsRes.ok) {
        const fetchedLeads = leadsRes.data.leads || [];
        setLeads(fetchedLeads);
        // Auto-open the edit modal when arriving from the dashboard (?edit=<id>)
        const editId = searchParams.get("edit") || searchParams.get("lead");
        if (editId) {
          const target = fetchedLeads.find((l) => l.id === editId);
          if (target) {
            openEditLead(target);
            searchParams.delete("edit");
            searchParams.delete("lead");
            setSearchParams(searchParams, { replace: true });
          }
        }
      }
      if (companiesRes.ok) setCompanies(companiesRes.data.companies || []);
      if (ridersRes.ok) setRiders(ridersRes.data.riders || []);
      if (stagesRes.ok && stagesRes.data.stages?.length > 0) {
        setStages(stagesRes.data.stages.map((s) => ({ ...s, color: resolveColor(s.color) })));
      }
      if (sourcesRes.ok && sourcesRes.data.sources?.length > 0) setLeadSources(sourcesRes.data.sources);
      if (associatesRes.ok) setAssociates(associatesRes.data.associates || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---- Lead add / edit ----
  const openAddLead = () => {
    setEditingLeadId(null);
    setLeadForm(EMPTY_LEAD_FORM);
    setShowLeadModal(true);
  };

  const openEditLead = (lead) => {
    setEditingLeadId(lead.id);
    setLeadForm({
      name: lead.name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      source: lead.source || "direct",
      insurance_type: lead.insurance_type || "health",
      associate_name: lead.associate_name || "",
      notes: lead.notes || "",
    });
    setShowLeadModal(true);
  };

  const handleSaveLead = async () => {
    if (!leadForm.name.trim()) {
      toast.error("Lead name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...leadForm,
        associate_name: leadForm.source === "associate" ? leadForm.associate_name : "",
      };
      let res;
      if (editingLeadId) {
        res = await authPut(`/api/advisor/leads/${editingLeadId}`, payload);
      } else {
        res = await authPost("/api/advisor/leads", { ...payload, status: stages[0]?.id || "new" });
      }
      if (res.ok) {
        toast.success(editingLeadId ? "Lead updated!" : "Lead created!");
        setShowLeadModal(false);
        setEditingLeadId(null);
        setLeadForm(EMPTY_LEAD_FORM);
        fetchData();
      } else {
        throw new Error(res.data?.detail || "Failed to save lead");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!editingLeadId) return;
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    setSaving(true);
    try {
      const res = await authDelete(`/api/advisor/leads/${editingLeadId}`);
      if (res.ok) {
        toast.success("Lead deleted");
        setShowLeadModal(false);
        setEditingLeadId(null);
        setLeadForm(EMPTY_LEAD_FORM);
        fetchData();
      } else {
        toast.error(res.data?.detail || "Failed to delete lead");
      }
    } catch (error) {
      toast.error("Failed to delete lead");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      const res = await authPut(`/api/advisor/leads/${leadId}`, { status: newStatus });
      if (res.ok) {
        setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
        toast.success("Status updated!");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // ---- Settings: Companies ----
  const handleAddCompany = async () => {
    if (!newCompany.trim()) return;
    try {
      const res = await authPost("/api/advisor/settings/companies", { name: newCompany });
      if (res.ok) {
        setCompanies([...companies, res.data.company]);
        setNewCompany("");
      }
    } catch (error) {
      toast.error("Failed to add company");
    }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await authDelete(`/api/advisor/settings/companies/${id}`);
      setCompanies(companies.filter((c) => c.id !== id));
    } catch (error) {
      toast.error("Failed to remove company");
    }
  };

  // ---- Settings: Riders ----
  const handleAddRider = async () => {
    if (!newRider.trim()) return;
    try {
      const res = await authPost("/api/advisor/settings/riders", { name: newRider });
      if (res.ok) {
        setRiders([...riders, res.data.rider]);
        setNewRider("");
      }
    } catch (error) {
      toast.error("Failed to add rider");
    }
  };

  const handleDeleteRider = async (id) => {
    try {
      await authDelete(`/api/advisor/settings/riders/${id}`);
      setRiders(riders.filter((r) => r.id !== id));
    } catch (error) {
      toast.error("Failed to remove rider");
    }
  };

  // ---- Settings: Stages ----
  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    try {
      const res = await authPost("/api/advisor/settings/stages", {
        name: newStageName,
        color: newStageColor,
        order: stages.length + 1,
      });
      if (res.ok) {
        setStages([...stages, res.data.stage]);
        setNewStageName("");
        setNewStageColor("#3b82f6");
        toast.success("Stage added!");
      }
    } catch (error) {
      toast.error("Failed to add stage");
    }
  };

  const handleDeleteStage = async (id) => {
    if (stages.length <= 2) {
      toast.error("You need at least 2 stages");
      return;
    }
    const res = await authDelete(`/api/advisor/settings/stages/${id}`);
    if (res.ok) {
      const moved = res.data?.moved_leads || 0;
      toast.success(moved > 0 ? `Stage removed · ${moved} lead${moved > 1 ? "s" : ""} moved to first stage` : "Stage removed");
      await fetchData();
    } else {
      toast.error(res.data?.detail || "Failed to remove stage");
    }
  };

  const handleUpdateStage = async (id, updates) => {
    try {
      const res = await authPut(`/api/advisor/settings/stages/${id}`, updates);
      if (res.ok) {
        setStages(stages.map((s) => (s.id === id ? { ...s, ...updates } : s)));
        toast.success("Stage updated");
        return true;
      }
      toast.error(res.data?.detail || "Failed to update stage");
      return false;
    } catch (error) {
      toast.error("Failed to update stage");
      return false;
    }
  };

  // ---- Settings: Lead Sources ----
  const handleAddSource = async () => {
    if (!newSource.trim()) return;
    try {
      const res = await authPost("/api/advisor/settings/sources", { name: newSource });
      if (res.ok) {
        setLeadSources([...leadSources, res.data.source]);
        setNewSource("");
        toast.success("Source added!");
      }
    } catch (error) {
      toast.error("Failed to add source");
    }
  };

  const handleDeleteSource = async (id) => {
    if (leadSources.length <= 1) {
      toast.error("You need at least 1 source");
      return;
    }
    try {
      const res = await authDelete(`/api/advisor/settings/sources/${id}`);
      if (res.ok) {
        setLeadSources(leadSources.filter((s) => (s.id || s.value) !== id));
        toast.success("Source removed");
      } else {
        toast.error(res.data?.detail || "Failed to remove source");
      }
    } catch (error) {
      toast.error("Failed to remove source");
    }
  };

  // ---- Settings: Associates ----
  const handleAddAssociate = async (name = null) => {
    const associateName = name || newAssociate.trim();
    if (!associateName) return null;
    try {
      const res = await authPost("/api/advisor/settings/associates", { name: associateName });
      if (res.ok) {
        setAssociates([...associates, res.data.associate]);
        setNewAssociate("");
        toast.success("Associate added!");
        return res.data.associate;
      }
    } catch (error) {
      toast.error("Failed to add associate");
    }
    return null;
  };

  const handleDeleteAssociate = async (id) => {
    try {
      await authDelete(`/api/advisor/settings/associates/${id}`);
      setAssociates(associates.filter((a) => a.id !== id));
      toast.success("Associate removed");
    } catch (error) {
      toast.error("Failed to remove associate");
    }
  };

  const getStageInfo = (stageId) => stages.find((s) => s.id === stageId) || stages[0] || { name: stageId, color: "#64748b" };

  const filteredLeads = leads.filter((lead) => {
    const leadIsInactive = isInactiveStage(lead.status) || stages.some((s) => s.id === lead.status && isInactiveStageObject(s));
    const leadIsConverted = isConvertedStage(lead.status) || stages.some((s) => s.id === lead.status && isConvertedStageObject(s));
    const leadIsPostponed = isPostponedStage(lead.status) || stages.some((s) => s.id === lead.status && isPostponedStageObject(s));

    if (leadTypeFilter === "active" && (leadIsInactive || leadIsConverted || leadIsPostponed)) return false;
    if (leadTypeFilter === "inactive" && !leadIsInactive) return false;
    if (leadTypeFilter === "converted" && !leadIsConverted) return false;
    if (leadTypeFilter === "postponed" && !leadIsPostponed) return false;

    if (insuranceTypeFilter !== "all" && (lead.insurance_type || "") !== insuranceTypeFilter) return false;

    if (!searchQuery) return statusFilter === "all" || lead.status === statusFilter;
    const query = searchQuery.toLowerCase();
    const matchesSearch = lead.name?.toLowerCase().includes(query) || lead.phone?.includes(query);
    return matchesSearch && (statusFilter === "all" || lead.status === statusFilter);
  });

  const isLeadConverted = (lead) =>
    isConvertedStage(lead.status) || stages.some((s) => s.id === lead.status && isConvertedStageObject(s));
  const isLeadInactive = (lead) =>
    isInactiveStage(lead.status) || stages.some((s) => s.id === lead.status && isInactiveStageObject(s));
  const isLeadPostponed = (lead) =>
    isPostponedStage(lead.status) || stages.some((s) => s.id === lead.status && isPostponedStageObject(s));
  const isLeadNonActive = (lead) => isLeadInactive(lead) || isLeadConverted(lead) || isLeadPostponed(lead);

  const activeLeadsCount = leads.filter((lead) => !isLeadNonActive(lead)).length;
  const inactiveLeadsCount = leads.filter((lead) => isLeadInactive(lead)).length;
  const convertedLeadsCount = leads.filter((lead) => isLeadConverted(lead)).length;
  const postponedLeadsCount = leads.filter((lead) => isLeadPostponed(lead)).length;

  const baseTypeLeads = leads.filter((lead) => {
    if (leadTypeFilter === "active") return !isLeadNonActive(lead);
    if (leadTypeFilter === "inactive") return isLeadInactive(lead);
    if (leadTypeFilter === "converted") return isLeadConverted(lead);
    if (leadTypeFilter === "postponed") return isLeadPostponed(lead);
    return true;
  });
  const insTypeCount = (t) =>
    t === "all" ? baseTypeLeads.length : baseTypeLeads.filter((l) => (l.insurance_type || "") === t).length;

  const activeStages = stages.filter((s) => !isInactiveStageObject(s) && !isConvertedStageObject(s) && !isPostponedStageObject(s));
  const inactiveStages = stages.filter((s) => isInactiveStageObject(s));
  const convertedStages = stages.filter((s) => isConvertedStageObject(s));
  const postponedStages = stages.filter((s) => isPostponedStageObject(s));
  const getLeadsCountByStage = (stageId) => leads.filter((l) => l.status === stageId).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#A87608]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] via-[#E7EDF6] to-[#EDF1F8] flex flex-col crm-page-container">
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#C9D4E6] sticky top-0 z-40 shrink-0">
        <div className="px-4 py-3">
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/advisor/dashboard")} className="p-2 -ml-2 rounded-lg hover:bg-[#EDF1F8]">
                <ArrowLeft className="h-5 w-5 text-[#1B2A4A]" />
              </button>
              <h1 className="text-base font-bold text-[#1B2A4A]">Leads</h1>
            </div>
            <div className="flex items-center gap-2">
              {canManage && (
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className="p-2 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4]"
                  data-testid="lead-settings-btn-mobile"
                >
                  <Settings className="h-5 w-5 text-[#3A4557]" />
                </button>
              )}
              {canEditLeads && (
                <button onClick={openAddLead} className="p-2 bg-blue-500 rounded-lg" data-testid="add-lead-btn-mobile">
                  <Plus className="h-5 w-5 text-white" />
                </button>
              )}
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/advisor/dashboard")} className="text-[#3A4557] hover:text-[#1B2A4A]">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/advisor/dashboard")} className="text-[#3A4557] hover:text-[#1B2A4A] px-2" title="Go to Main Dashboard">
                <Home className="h-4 w-4" />
              </Button>
              <div className="h-6 w-px bg-[#E2E9F4]" />
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#2E3B57]" />
                </div>
                <h1 className="text-lg font-bold text-[#1B2A4A]">Leads</h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {canManage && (
                <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)} className="border-[#C9D4E6] text-[#3A4557]" data-testid="lead-settings-btn">
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              {canEditLeads && (
                <Button onClick={openAddLead} size="sm" className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-lead-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Lead
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="flex-1 overflow-y-auto p-3 md:p-4 pb-28 md:pb-6">
        {/* Active/Won/Lost toggle */}
        <div className="flex items-center justify-between mb-4">
          <Swipeable className="flex items-center bg-[#EDF1F8] rounded-lg p-1 w-max">
            <button
              onClick={() => { setLeadTypeFilter("active"); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${leadTypeFilter === "active" ? "bg-blue-500 text-white" : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"}`}
              data-testid="lead-filter-active"
            >
              <span className="w-2 h-2 rounded-full bg-green-400" />
              Active
              <span className={`px-1.5 py-0.5 rounded text-xs ${leadTypeFilter === "active" ? "bg-blue-400/30" : "bg-[#E2E9F4]"}`}>{activeLeadsCount}</span>
            </button>
            <button
              onClick={() => { setLeadTypeFilter("converted"); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${leadTypeFilter === "converted" ? "bg-emerald-500 text-white" : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"}`}
              data-testid="lead-filter-converted"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Won
              <span className={`px-1.5 py-0.5 rounded text-xs ${leadTypeFilter === "converted" ? "bg-emerald-400/30" : "bg-[#E2E9F4]"}`}>{convertedLeadsCount}</span>
            </button>
            <button
              onClick={() => { setLeadTypeFilter("inactive"); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${leadTypeFilter === "inactive" ? "bg-red-500 text-white" : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"}`}
              data-testid="lead-filter-inactive"
            >
              <span className="w-2 h-2 rounded-full bg-red-400" />
              Lost
              <span className={`px-1.5 py-0.5 rounded text-xs ${leadTypeFilter === "inactive" ? "bg-red-400/30" : "bg-[#E2E9F4]"}`}>{inactiveLeadsCount}</span>
            </button>
            <button
              onClick={() => { setLeadTypeFilter("postponed"); setStatusFilter("all"); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${leadTypeFilter === "postponed" ? "bg-orange-500 text-white" : "text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#E2E9F4]"}`}
              data-testid="lead-filter-postponed"
            >
              <span className="w-2 h-2 rounded-full bg-orange-400" />
              Postponed
              <span className={`px-1.5 py-0.5 rounded text-xs ${leadTypeFilter === "postponed" ? "bg-orange-400/30" : "bg-[#E2E9F4]"}`}>{postponedLeadsCount}</span>
            </button>
          </Swipeable>
        </div>

        {/* Insurance Type Filter */}
        <Swipeable outerClassName="mb-3" className="flex items-center gap-2 pb-1 -mx-3 px-3 md:mx-0 md:px-0" testId="insurance-type-filter">
          <span className="text-[#3A4557] text-xs shrink-0">Type:</span>
          <button
            onClick={() => setInsuranceTypeFilter("all")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 transition-colors ${insuranceTypeFilter === "all" ? "bg-white/90 text-[#0D1A3A] border border-[#C9D4E6]" : "bg-[#EDF1F8]/60 text-[#3A4557] hover:text-[#1B2A4A]"}`}
            data-testid="ins-filter-all"
          >
            All <span className="font-bold">{insTypeCount("all")}</span>
          </button>
          {INSURANCE_TYPES.map((t) => {
            const active = insuranceTypeFilter === t.value;
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                onClick={() => setInsuranceTypeFilter(active ? "all" : t.value)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 transition-all border"
                style={{ backgroundColor: active ? t.color : t.bg, color: active ? "#FFFFFF" : t.color, borderColor: active ? t.color : "transparent" }}
                data-testid={`ins-filter-${t.value}`}
              >
                <Icon className="h-3 w-3" />
                {t.label} <span className="font-bold">{insTypeCount(t.value)}</span>
              </button>
            );
          })}
        </Swipeable>

        {/* Stage pills */}
        <Swipeable outerClassName="mb-4" className="flex items-center gap-2 pb-2 -mx-3 px-3 md:mx-0 md:px-0">
          <span className="text-[#3A4557] text-xs shrink-0">Stages:</span>
          {canEditLeads && (
            <button
              onClick={() => setShowStagesModal(true)}
              className="shrink-0 p-1 rounded-md hover:bg-[#EDF1F8] text-[#2E3B57] hover:text-[#1B2A4A] transition-colors"
              title="Manage lead stages"
              data-testid="manage-stages-btn"
            >
              <Settings className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="flex items-center gap-1.5">
            {(leadTypeFilter === "active" ? activeStages : leadTypeFilter === "converted" ? convertedStages : leadTypeFilter === "postponed" ? postponedStages : inactiveStages).map((stage) => {
              const count = getLeadsCountByStage(stage.id);
              const isActive = statusFilter === stage.id;
              const textColor = getContrastTextColor(stage.color);
              return (
                <button
                  key={stage.id}
                  onClick={() => setStatusFilter(isActive ? "all" : stage.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-all shrink-0 font-medium"
                  style={{
                    backgroundColor: isActive ? stage.color : "rgba(20, 33, 61, 0.06)",
                    color: isActive ? textColor : "#44506B",
                    border: isActive ? "1px solid transparent" : "1px solid rgba(20, 33, 61, 0.12)",
                  }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span className="truncate max-w-[80px]">{stage.name}</span>
                  <span className="font-bold">{count}</span>
                </button>
              );
            })}
          </div>
        </Swipeable>

        {/* Search */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <Input
              placeholder={leadTypeFilter === "active" ? "Search active leads..." : leadTypeFilter === "converted" ? "Search won leads..." : leadTypeFilter === "postponed" ? "Search postponed leads..." : "Search lost leads..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 bg-[#EDF1F8]/50 border-[#C9D4E6] text-[#1B2A4A] text-sm"
              data-testid="lead-search-input"
            />
          </div>
          {statusFilter !== "all" && (
            <button onClick={() => setStatusFilter("all")} className="flex items-center gap-1 px-2 py-1.5 bg-[#EDF1F8] rounded-lg text-xs text-[#3A4557] hover:bg-[#E2E9F4]">
              <X className="h-3 w-3" />
              Clear Filter
            </button>
          )}
        </div>

        {/* Leads list */}
        <div className="bg-white/50 border border-[#C9D4E6] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#C9D4E6]">
            <h3 className="text-[#1B2A4A] font-semibold text-sm flex items-center gap-2">
              {statusFilter === "all"
                ? leadTypeFilter === "active" ? "Active Leads" : leadTypeFilter === "converted" ? "Won Leads" : leadTypeFilter === "postponed" ? "Postponed Leads" : "Lost Leads"
                : `${getStageInfo(statusFilter).name}`}
              <span className="text-[#3A4557] font-normal">({filteredLeads.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-[#C9D4E6]/50">
            {filteredLeads.length === 0 ? (
              <div className="text-center py-10">
                <Users className="h-10 w-10 text-[#3A4557] mx-auto mb-2" />
                <p className="text-[#3A4557] text-sm">No leads found</p>
                {leadTypeFilter === "active" && canEditLeads && (
                  <Button onClick={openAddLead} size="sm" className="mt-3 bg-blue-500 hover:bg-blue-600 text-white" data-testid="add-lead-empty-state-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Lead
                  </Button>
                )}
              </div>
            ) : (
              filteredLeads.map((lead) => {
                const stageInfo = getStageInfo(lead.status);
                const textColor = getContrastTextColor(stageInfo.color);
                return (
                  <div key={lead.id} className="flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-[#EDF1F8]/40 transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <button onClick={() => openEditLead(lead)} className="flex items-center gap-2.5 min-w-0 flex-1 text-left" data-testid={`lead-open-${lead.id}`}>
                      <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 border border-[#C9D4E6]" style={{ backgroundColor: stageInfo.color + "30" }}>
                        <span className="font-semibold text-sm text-[#1B2A4A]">{lead.name?.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[#1B2A4A] font-medium text-sm truncate max-w-[140px]">{lead.name}</p>
                          <InsuranceTypeBadge type={lead.insurance_type} />
                          <SourceBadge lead={lead} sources={leadSources} />
                        </div>
                        <p className="text-[#3A4557] text-xs truncate">{lead.phone || lead.email || "No contact"}</p>
                      </div>
                    </button>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Inline stage dropdown */}
                      <Select value={lead.status} onValueChange={(value) => handleUpdateLeadStatus(lead.id, value)}>
                        <SelectTrigger
                          className="h-6 min-w-[92px] max-w-[130px] border-0 text-[10px] font-medium rounded-full px-2"
                          style={{ backgroundColor: stageInfo.color, color: textColor }}
                          data-testid={`lead-stage-select-${lead.id}`}
                        >
                          <span style={{ color: textColor }} className="truncate">{stageInfo.name}</span>
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#C9D4E6]">
                          {stages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.id} className="text-xs">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                                <span className="text-[#1B2A4A]">{stage.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => openEditLead(lead)} className="p-1.5 rounded-md hover:bg-[#EDF1F8] text-[#2E3B57]" title="Edit lead" data-testid={`lead-edit-btn-${lead.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>

      {/* Add / Edit Lead Modal */}
      <Dialog open={showLeadModal} onOpenChange={(o) => { setShowLeadModal(o); if (!o) setEditingLeadId(null); }}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md max-h-[90vh] overflow-y-auto" data-testid="lead-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              {editingLeadId ? <Pencil className="h-5 w-5 text-[#2E3B57]" /> : <UserPlus className="h-5 w-5 text-[#2E3B57]" />}
              {editingLeadId ? "Edit Lead" : "Add New Lead"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#3A4557] text-xs">Name *</Label>
              <Input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="Lead name" className="mt-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" data-testid="lead-name-input" />
            </div>
            <div>
              <Label className="text-[#3A4557] text-xs">Insurance Type *</Label>
              <div className="grid grid-cols-4 gap-2 mt-1.5" data-testid="lead-insurance-type">
                {INSURANCE_TYPES.map((t) => {
                  const active = leadForm.insurance_type === t.value;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setLeadForm({ ...leadForm, insurance_type: t.value })}
                      className="flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-all"
                      style={{ backgroundColor: active ? t.color : t.bg, color: active ? "#FFFFFF" : t.color, borderColor: active ? t.color : "transparent" }}
                      data-testid={`lead-type-${t.value}`}
                    >
                      <Icon className="h-4 w-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#3A4557] text-xs">Phone</Label>
                <Input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="Phone" className="mt-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" data-testid="lead-phone-input" />
              </div>
              <div>
                <Label className="text-[#3A4557] text-xs">Reference Source</Label>
                <Select value={leadForm.source} onValueChange={(v) => setLeadForm({ ...leadForm, source: v })}>
                  <SelectTrigger className="mt-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A]" data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    {leadSources.map((s) => (
                      <SelectItem key={s.id || s.value} value={s.value || s.id}>{s.label || s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Associate name — shown only when source is "associate" */}
            {leadForm.source === "associate" && (
              <div className="p-3 bg-purple-900/10 rounded-lg border border-purple-800/30">
                <Label className="text-purple-700 text-xs flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" />
                  Associate Name
                </Label>
                <div className="flex gap-2">
                  <Select value={leadForm.associate_name} onValueChange={(v) => setLeadForm({ ...leadForm, associate_name: v })}>
                    <SelectTrigger className="flex-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A]" data-testid="lead-associate-select">
                      <SelectValue placeholder="Select associate..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#C9D4E6]">
                      {associates.map((a) => (
                        <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={newAssociate}
                    onChange={(e) => setNewAssociate(e.target.value)}
                    placeholder="Or add new..."
                    className="w-32 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] text-sm placeholder:text-[#3A4557]"
                    onKeyPress={async (e) => {
                      if (e.key === "Enter" && newAssociate.trim()) {
                        const newA = await handleAddAssociate();
                        if (newA) setLeadForm((prev) => ({ ...prev, associate_name: newA.name }));
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (newAssociate.trim()) {
                        const newA = await handleAddAssociate();
                        if (newA) setLeadForm((prev) => ({ ...prev, associate_name: newA.name }));
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {associates.length === 0 && !newAssociate && (
                  <p className="text-purple-600/70 text-xs mt-2">No associates yet. Add one above.</p>
                )}
              </div>
            )}

            <div>
              <Label className="text-[#3A4557] text-xs">Email</Label>
              <Input value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="Email" className="mt-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" data-testid="lead-email-input" />
            </div>
            <div>
              <Label className="text-[#3A4557] text-xs">Notes</Label>
              <Input value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} placeholder="Optional notes" className="mt-1 bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" data-testid="lead-notes-input" />
            </div>

            <div className="flex gap-3 pt-2">
              {editingLeadId && canManage && (
                <Button variant="outline" onClick={handleDeleteLead} disabled={saving} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="lead-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowLeadModal(false)} className="flex-1 border-[#C9D4E6]">Cancel</Button>
              <Button onClick={handleSaveLead} disabled={saving} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white" data-testid="lead-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : editingLeadId ? <Check className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {editingLeadId ? "Save" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal (Admin) */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2"><Settings className="h-5 w-5 text-[#A87608]" />Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Lead Stages */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4" />Lead Stages (Customizable)</Label>
              <div className="space-y-2 mb-3">
                {stages.map((stage) => (
                  <div key={stage.id} className="flex items-center gap-2 p-2 bg-[#EDF1F8]/50 rounded">
                    <GripVertical className="h-4 w-4 text-[#3A4557]" />
                    <div className="h-4 w-4 rounded" style={{ backgroundColor: stage.color }} />
                    <span className="text-[#1B2A4A] text-sm flex-1">{stage.name}</span>
                    <button onClick={() => handleDeleteStage(stage.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={newStageColor} onChange={(e) => setNewStageColor(e.target.value)} className="h-8 w-8 rounded cursor-pointer" />
                <Input value={newStageName} onChange={(e) => setNewStageName(e.target.value)} placeholder="New stage name..." className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" onKeyPress={(e) => e.key === "Enter" && handleAddStage()} />
                <Button size="sm" onClick={handleAddStage} className="h-8 bg-blue-500 text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Companies */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><Building2 className="h-4 w-4" />Insurance Companies</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {companies.map((c) => (
                  <span key={c.id} className="flex items-center gap-1 bg-[#EDF1F8] text-[#2E3B57] text-xs px-2 py-1 rounded">
                    {c.name}<button onClick={() => handleDeleteCompany(c.id)} className="text-red-600 ml-1"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="Add company..." className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" onKeyPress={(e) => e.key === "Enter" && handleAddCompany()} />
                <Button size="sm" onClick={handleAddCompany} className="h-8 bg-blue-500 text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Riders */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><Shield className="h-4 w-4" />Riders / Add-ons</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {riders.map((r) => (
                  <span key={r.id} className="flex items-center gap-1 bg-[#EDF1F8] text-[#2E3B57] text-xs px-2 py-1 rounded">
                    {r.name}<button onClick={() => handleDeleteRider(r.id)} className="text-red-600 ml-1"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newRider} onChange={(e) => setNewRider(e.target.value)} placeholder="Add rider..." className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" onKeyPress={(e) => e.key === "Enter" && handleAddRider()} />
                <Button size="sm" onClick={handleAddRider} className="h-8 bg-green-600 text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Lead Sources */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><GitBranch className="h-4 w-4" />Reference Sources</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {leadSources.map((s) => (
                  <span key={s.id || s.value} className="flex items-center gap-1 bg-purple-900/20 text-purple-700 text-xs px-2 py-1 rounded border border-purple-800/40">
                    {s.label || s.name}
                    <button onClick={() => handleDeleteSource(s.id || s.value)} className="text-red-600 ml-1 hover:text-red-700"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newSource} onChange={(e) => setNewSource(e.target.value)} placeholder="Add reference source..." className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" onKeyPress={(e) => e.key === "Enter" && handleAddSource()} />
                <Button size="sm" onClick={handleAddSource} className="h-8 bg-purple-600 text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Associates */}
            <div>
              <Label className="text-[#3A4557] text-xs flex items-center gap-2 mb-2"><Users className="h-4 w-4" />Associates</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {associates.map((a) => (
                  <span key={a.id} className="flex items-center gap-1 bg-cyan-900/20 text-cyan-700 text-xs px-2 py-1 rounded border border-cyan-800/40">
                    {a.name}
                    <button onClick={() => handleDeleteAssociate(a.id)} className="text-red-600 ml-1 hover:text-red-700"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newAssociate} onChange={(e) => setNewAssociate(e.target.value)} placeholder="Add associate..." className="flex-1 h-8 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" onKeyPress={(e) => e.key === "Enter" && handleAddAssociate()} />
                <Button size="sm" onClick={() => handleAddAssociate()} className="h-8 bg-cyan-600 text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Stages Modal — Add / Edit / Delete */}
      <Dialog open={showStagesModal} onOpenChange={(o) => { setShowStagesModal(o); if (!o) setEditingStageId(null); }}>
        <DialogContent className="bg-white border-[#C9D4E6] max-w-md max-h-[85vh] overflow-y-auto" data-testid="manage-stages-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#A87608]" />Manage Lead Stages</DialogTitle>
          </DialogHeader>
          <div className="mt-3">
            <p className="text-[#3A4557] text-xs mb-3">Tap a stage to rename or recolor it. Deleting a stage moves any leads on it to the first stage.</p>
            <div className="space-y-2 mb-4">
              {stages.map((stage) => {
                const isEditing = editingStageId === stage.id;
                return (
                  <div key={stage.id} className="p-2 bg-[#EDF1F8]/50 rounded-lg" data-testid={`stage-row-${stage.id}`}>
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input type="color" value={editStageColor} onChange={(e) => setEditStageColor(e.target.value)} className="h-9 w-9 rounded cursor-pointer bg-transparent border border-[#C9D4E6]" data-testid={`stage-edit-color-${stage.id}`} />
                          <Input value={editStageName} onChange={(e) => setEditStageName(e.target.value)} placeholder="Stage name" className="flex-1 h-9 text-sm bg-white border-[#C9D4E6] text-[#1B2A4A]" data-testid={`stage-edit-name-${stage.id}`} autoFocus />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingStageId(null)} className="flex-1 h-8 border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]" data-testid={`stage-edit-cancel-${stage.id}`}>Cancel</Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              const name = editStageName.trim();
                              if (!name) { toast.error("Name cannot be empty"); return; }
                              const ok = await handleUpdateStage(stage.id, { name, color: editStageColor });
                              if (ok) setEditingStageId(null);
                            }}
                            className="flex-1 h-8 bg-[#D4A017] hover:bg-[#D9A82C] text-black font-semibold"
                            data-testid={`stage-edit-save-${stage.id}`}
                          >
                            <Check className="h-3.5 w-3.5 mr-1" /> Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 rounded shrink-0 border border-[#C9D4E6]" style={{ backgroundColor: stage.color }} />
                        <span className="text-[#1B2A4A] text-sm flex-1 truncate">{stage.name}</span>
                        <button
                          onClick={() => { setEditingStageId(stage.id); setEditStageName(stage.name); setEditStageColor(stage.color || "#3b82f6"); }}
                          className="text-[#2E3B57] hover:text-[#1B2A4A] p-1 rounded hover:bg-[#EDF1F8]"
                          title="Edit stage"
                          data-testid={`stage-edit-btn-${stage.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`Delete stage "${stage.name}"? Leads on it will move to the first stage.`)) handleDeleteStage(stage.id); }}
                          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-500/10"
                          title="Delete stage"
                          data-testid={`stage-delete-btn-${stage.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="pt-3 border-t border-[#C9D4E6]">
              <Label className="text-[#3A4557] text-xs mb-2 block">Add new stage</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={newStageColor} onChange={(e) => setNewStageColor(e.target.value)} className="h-9 w-9 rounded cursor-pointer bg-transparent border border-[#C9D4E6]" data-testid="new-stage-color" />
                <Input value={newStageName} onChange={(e) => setNewStageName(e.target.value)} placeholder="New stage name..." onKeyPress={(e) => e.key === "Enter" && handleAddStage()} className="flex-1 h-9 text-sm bg-[#EDF1F8] border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557]" data-testid="new-stage-name" />
                <Button size="sm" onClick={handleAddStage} className="h-9 bg-[#D4A017] hover:bg-[#D9A82C] text-black font-semibold" data-testid="add-stage-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
